import { Dispatch, FC, SetStateAction, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import {
  IconButton,
  Box,
  Stack,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  useMediaQuery,
} from '@mui/material';
import { DataGrid, GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteDialog from 'components/DeleteDialog';
import moment from 'moment';
import { deleteRule, getRule } from 'api/tournamentsrules';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import TournamentsRulesDetails from './TournamentsRulesDetails';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import theme from 'themes/lightTheme';
import TournamentsRulesNewItemDialog from './TournamentsRulesNewItemDialog';

/**
 * Свойства компонента.
 */
type Props = {
  /**
   * Список элементов.
   */
  items: any;

  /**
   * ID правила для детального просмотра
   */
  rulesView: string; 
    
  /**
   * Изменить правило для детального просмотра
   */
  setRulesView: Dispatch<SetStateAction<string>>;
};

/**
 * Отображает таблицу со всеми правилами.
 */
const TournamentsRulesTable: FC<Props> = ({ items, rulesView, setRulesView }) => {
    const queryClient = useQueryClient();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [pageSize, setPageSize] = useState<number>(10);
    const [open, setOpen] = useState(false);
    const [newItem, setNewItem] = useState(false);
    const [item, setItem] = useState<number>(0);

    const { mutate, isLoading } = useMutation(deleteRule, {
        onSuccess: () => {
            queryClient.invalidateQueries('tournaments-rules-data');
        },
    });

    const {data: rulesets, isFetching} = useQuery(['tournaments-rulesets-data', rulesView], getRule, {
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        retry: false
    },)

    const columns: GridColDef[] = [
      { field: 'name', headerName: 'Правило', flex: 1, minWidth: 100, sortable: true },
      {
        field: 'created_at',
        headerName: 'Дата создания',
        sortable: false,
        valueGetter: (params: GridValueGetterParams) => `${moment(params.row.created_at).format('DD.MM.YYYY HH:mm')}`,
        flex: 1,
        minWidth: 150,
      },
      {
        field: "view",
        headerName: "Просмотр правила",
        sortable: false,
        disableColumnMenu: true,
        minWidth: 150,
        renderCell: (params) => {
            return (
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "center",
                  alignItems: "center"
                }}
              >
                <IconButton onClick={() => setRulesView(params.row.id)} disabled={isLoading}>
                  <VisibilityOutlinedIcon />
                </IconButton>
              </Box>
            );
        }
      },
      {
        field: "id",
        headerName: "",
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
            function deliteItem(id: number) {
              mutate(item);
              setOpen(false);
            }

            function openDialog (id: number) {
              setItem(id);
              setOpen(true);
            }

            return (
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "center",
                  alignItems: "center"
                }}
              >
                <IconButton onClick={() => openDialog(params.row.id)} disabled={isLoading}>
                  <DeleteIcon />
                </IconButton>
                <DeleteDialog close={() => setOpen(false)} open={open} handleAgreeAction={() => deliteItem(params.row.id)}/>
              </Box>
            );
        }
      }
    ];

  return (
    <>
        {rulesView && (
            <Box className='flex flex-col gap-4 p-3'>
                <Box className='flex flex-row items-center justify-between'>
                    <Box className='flex flex-row items-center'>
                        <Box>
                            <IconButton onClick={() => setRulesView(null)} disabled={isLoading} size="small">
                                <ArrowBackOutlinedIcon />
                            </IconButton>
                        </Box>
                        <Typography className='ml-2'>
                            {rulesets?.name}
                        </Typography>
                    </Box>
                    <Button onClick={() => setNewItem(true)} variant="contained" className="align-middle rounded-full" endIcon={<AddBoxOutlinedIcon />}>Правило для метки</Button>
                </Box>
                <Box>
                    {isFetching && <Box className='flex items-center justify-center h-32'><CircularProgress /></Box>}
                    {!isFetching && rulesets && (
                        <TournamentsRulesDetails rulesets={rulesets.rulesets} markId={rulesets.id} />
                    )}
                </Box>
            </Box>
        )}
        {!rulesView && (
            <Box
                className="h-auto overflow-auto"
                sx={{
                '& .active': {
                    backgroundColor: '#fcecd5',
                    color: '#1a3e72',
                },
                }}
            >
                <DataGrid
                    autoHeight={true}
                    rows={items}
                    columns={columns}
                    pageSize={pageSize}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    pagination
                    disableSelectionOnClick
                    disableColumnMenu
                    initialState={{
                      sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
                    }}
                    components={{
                    NoRowsOverlay: () => (
                        <Stack height="100%" alignItems="center" justifyContent="center">
                        Правила отсутствуют
                        </Stack>
                    ),
                    }}
                />
          </Box>
        )}
        <Dialog fullWidth maxWidth='sm' open={newItem} onClose={() => setNewItem(false)} fullScreen={fullScreen}>
            <TournamentsRulesNewItemDialog close={() => setNewItem(false)} id={rulesView} />
        </Dialog>
    </>
    
  );
};

export default TournamentsRulesTable;
